/**
 * GiftGo — invisible Lottie players must not tick.
 *
 * Pages are mounted once and hidden with `display:none` (router show/hide,
 * `prefetchAll()` mounts every page up front). A `<tgs-player autoplay loop>`
 * inside such a page has no box and no viewer — but it plays: lottie-player's
 * own IntersectionObserver fires once, while the sticker is still loading, and
 * never again, so `autoplay` starts it after load and nothing stops it. One
 * such player (`no_have_friends.tgs`, profile page) kept lottie-web's rAF loop
 * alive on EVERY screen — 5.6 pp of the phone's main thread in full idle
 * (measured 2026-09-20).
 *
 * The guard: a capture listener for the player's own `play` event. A player
 * that starts playing while it has no layout box (`getClientRects()` is empty —
 * a `display:none` ancestor) is frozen right there. `freeze()` is the library's
 * own state for "hidden": its IntersectionObserver plays it again the moment
 * the page (or tab) is shown — no code of ours is needed on the way back.
 *
 * Nothing visible changes: a frozen player is one nobody can see.
 */

const PLAYER_TAGS = new Set(['TGS-PLAYER', 'LOTTIE-PLAYER']);

/** Has no layout box at all: `display:none` on the element or an ancestor. */
function hasNoBox(el) {
  try {
    return el.getClientRects().length === 0;
  } catch (_) {
    return false;
  }
}

function onPlay(e) {
  const el = e.target;
  if (!el || !PLAYER_TAGS.has(el.tagName) || typeof el.freeze !== 'function') return;
  // freeze only on the way UP: the library dispatches `play` after it has set
  // its state, so a freeze here wins the race with `autoplay`
  if (hasNoBox(el)) {
    try { el.freeze(); } catch (_) { /* a player mid-teardown — nothing to do */ }
  }
}

let installed = false;

/** Install once per document. Safe to call twice. */
export function installHiddenLottieGuard() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  // the player's `play` is a non-bubbling CustomEvent: capture phase still
  // sees it at the document
  document.addEventListener('play', onPlay, true);
}

export default { installHiddenLottieGuard };
